import React, { useContext, useState } from "react";
import styled from "styled-components";
import {
  calculateDeliveryDate,
  workingHoursCutoff,
} from "../../utils/deliveryDate";
import { useTranslation } from "react-i18next";
import {
  qualifiesForFreeShipping,
  transportMethods,
} from "../../utils/global_const";
import { ProductContext } from "../../context/ProductContext";
import { useSelector } from "react-redux";
import {
  Card,
  CardTitle,
  RadioCard,
  RadioCircle,
  RadioContent,
  RadioTitle,
  RadioPrice,
  InfoBanner,
  InfoBannerLabel,
  InfoBannerText,
  PrimaryButton,
  ButtonRow,
} from "./CheckoutUI";

const InfoIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: var(--bg-100);
  color: var(--primary-100);
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
`;

const MethodInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const EstimateBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  width: fit-content;
  padding: 2px 10px;
  border-radius: 999px;
  background-color: var(--bg-200);
  color: var(--text-200);
  font-size: var(--font-size-small);
`;

const FreePrice = styled(RadioPrice)`
  color: var(--success-color);
`;

const CutoffNote = styled.p`
  margin: var(--spacing-xs) 0 0 34px;
  font-size: var(--font-size-small);
  color: var(--accent-200);
`;

const PinIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M5 9.5C5 6.09371 8.00993 3 12 3C15.9901 3 19 6.09371 19 9.5C19 11.6449 17.6877 14.0406 15.9606 16.2611C14.5957 18.016 13.0773 19.5329 12 20.5944C10.9227 19.5329 9.40427 18.016 8.03935 16.2611C6.31229 14.0406 5 11.6449 5 9.5ZM12 1C6.99007 1 3 4.90629 3 9.5C3 12.3551 4.68771 15.2094 6.46065 17.4889C7.99487 19.4615 9.7194 21.1574 10.7973 22.2173C10.9831 22.4001 11.1498 22.564 11.2929 22.7071C11.4804 22.8946 11.7348 23 12 23C12.2652 23 12.5196 22.8946 12.7071 22.7071C12.8502 22.564 13.0169 22.4001 13.2027 22.2174L13.2028 22.2173C14.2806 21.1573 16.0051 19.4615 17.5394 17.4889C19.3123 15.2094 21 12.3551 21 9.5C21 4.90629 17.0099 1 12 1ZM12 12.5C13.3807 12.5 14.5 11.3807 14.5 10C14.5 8.61929 13.3807 7.5 12 7.5C10.6193 7.5 9.5 8.61929 9.5 10C9.5 11.3807 10.6193 12.5 12 12.5Z"
      fill="currentColor"
    />
  </svg>
);

const ClockIcon = () => (
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
    <path
      d="M12 7V12L15 14"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Step2 = ({ onNext, address }) => {
  const { t } = useTranslation();
  const { currencyTag } = useContext(ProductContext);
  const [selectedMethod, setSelectedMethod] = useState(transportMethods[0]);
  const cartItems = useSelector((state) => state.cart.itemsList);
  const subtotalRounded = parseFloat(
    cartItems
      .reduce(
        (total, item) => total + item.product.discount_price * item.quantity,
        0
      )
      .toFixed(2)
  );

  const handleSelect = (method) => {
    setSelectedMethod(method);
  };

  const handleSubmit = () => {
    if (selectedMethod) {
      onNext(selectedMethod);
    }
  };

  return (
    <>
      <InfoBanner>
        <InfoRow>
          <InfoIcon>
            <PinIcon />
          </InfoIcon>
          <div>
            <InfoBannerLabel>{t("CHECKOUT.DELIVER_TO")}</InfoBannerLabel>
            <InfoBannerText>
              {`${address.street} ${address.street_number}, ${address.city} ${address.postal_code}, ${address.country}`}
            </InfoBannerText>
          </div>
        </InfoRow>
      </InfoBanner>

      <Card>
        <CardTitle>{t("CHECKOUT.DELIVERY_OPTIONS")}</CardTitle>
        {transportMethods.map((method, index) => {
          const isSelected = selectedMethod.name === method.name;
          const price = qualifiesForFreeShipping(subtotalRounded, method.name)
            ? 0
            : method.price;
          return (
            <div key={index}>
              <RadioCard
                $isSelected={isSelected}
                onClick={() => handleSelect(method)}
              >
                <RadioCircle $isSelected={isSelected} />
                <RadioContent>
                  <MethodInfo>
                    <RadioTitle>{method.name}</RadioTitle>
                    <EstimateBadge>
                      <ClockIcon />
                      {t("CHECKOUT.ESTIMATED_DELIVERY")}{" "}
                      {calculateDeliveryDate(method.days)}
                    </EstimateBadge>
                  </MethodInfo>
                  {price === 0 ? (
                    <FreePrice>{t("CHECKOUT.FREE")}</FreePrice>
                  ) : (
                    <RadioPrice>
                      {currencyTag}
                      {price.toFixed(2)}
                    </RadioPrice>
                  )}
                </RadioContent>
              </RadioCard>
              {isSelected && (
                <CutoffNote>
                  {t("CHECKOUT.PLACE_ORDER_BEFORE")} {workingHoursCutoff}h{" "}
                  {t("CHECKOUT.SAME_DAY_SHIPPING")}
                </CutoffNote>
              )}
            </div>
          );
        })}

        <ButtonRow>
          <PrimaryButton onClick={handleSubmit} disabled={!selectedMethod}>
            {t("BUTTONS.CONTINUE")}
          </PrimaryButton>
        </ButtonRow>
      </Card>
    </>
  );
};

export default Step2;
